import { placeTip } from '../../lib/tooltip.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- ui-tooltip ---
let __fez_component_0;
{
const DELAY = 100 // ms of hover before the first tooltip shows
const WARM = 400 // ms after one hides in which the next shows at once
const TARGET = '[title]:not(iframe):not([title=""])'
const ID = 'ui-tip'
__fez_component_0 = class {
  MOUNT = true

  init() {
    this.state.owner = null // the element whose title is held aside
    this.state.title = '' // its title as written, given back on release
    this.state.text = ''
    this.state.via = '' // 'pointer' or 'focus'
    this.state.muted = false // pressed or typed on: no tooltip until the owner changes
    this.state.added = [] // aria attributes set on the owner: {name, before, value}
    this.state.timer = 0
    this.state.hiddenAt = 0
    this.state.watch = new MutationObserver(() => this.retitle())
  }

  onMount() {
    const capture = { capture: true }
    this.on(document, 'pointerover', this.over, capture)
    this.on(document, 'pointerout', this.out, capture)
    this.on(document, 'focusin', this.focusIn, capture)
    this.on(document, 'focusout', this.focusOut, capture)
    this.on(document, 'pointerdown', this.mute, capture)
    this.on(document, 'keydown', this.mute, capture)
    this.on(document, 'scroll', this.cool, { capture: true, passive: true })
    this.on(window, 'resize', this.cool)
    this.on(window, 'blur', this.release)
    this.on(window, 'hashchange', this.release)
  }

  over(event) {
    if (event.pointerType === 'touch') return
    const owner = this.state.owner
    let el = event.target.closest?.(TARGET) || null
    // a titled link or button speaks for everything inside it (the account button, not its avatar's name)
    const action = (el || event.target).closest?.('a, button')
    if (action && (action === owner || action.matches(TARGET))) el = action
    // the owner has no title while held: inside it, it stays the target unless a titled child is nearer
    if (owner?.contains(event.target) && !(el && owner.contains(el))) el = owner
    if (el === owner) return
    if (!el) return this.release()
    this.take(el, 'pointer')
    this.schedule()
  }

  out(event) {
    if (!event.relatedTarget && this.state.via === 'pointer') this.release()
  }

  focusIn(event) {
    const el = event.target
    if (!el.matches?.(TARGET) || !el.matches(':focus-visible')) return
    this.take(el, 'focus')
    this.show()
  }

  focusOut(event) {
    if (event.target === this.state.owner && this.state.via === 'focus') this.release()
  }

  take(el, via) {
    this.restore()
    this.state.owner = el
    this.state.via = via
    this.state.muted = false
    this.state.title = el.getAttribute('title')
    this.state.text = this.state.title.trim()
    el.removeAttribute('title')
    this.describe(el)
    this.state.watch.observe(el, { attributes: true, attributeFilter: ['title'] })
  }

  // A title written while held (a re-render, a changed label) replaces the text.
  retitle() {
    const owner = this.state.owner
    if (!owner?.hasAttribute('title')) return
    this.state.title = owner.getAttribute('title')
    this.state.text = this.state.title.trim()
    owner.removeAttribute('title')
    this.undo(owner)
    this.describe(owner)
    if (this.isOpen()) this.show()
  }

  // Without its title the owner keeps its name (aria-label when nothing else names it)
  // and its description (the tooltip, through aria-describedby).
  describe(el) {
    const text = this.state.text
    const name = el.getAttribute('aria-label') || el.textContent.trim()
    if (!name && !el.hasAttribute('aria-labelledby')) {
      this.add(el, 'aria-label', text)
    } else if (name !== text) {
      const ids = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean)
      if (!ids.includes(ID)) this.add(el, 'aria-describedby', [...ids, ID].join(' '))
    }
  }

  add(el, name, value) {
    this.state.added.push({ name, before: el.getAttribute(name), value })
    el.setAttribute(name, value)
  }

  // Takes back only what is still ours: a value a re-render wrote meanwhile stays.
  undo(el) {
    for (const { name, before, value } of this.state.added.reverse()) {
      if (el.getAttribute(name) !== value) continue
      if (before === null) el.removeAttribute(name)
      else el.setAttribute(name, before)
    }
    this.state.added = []
  }

  restore() {
    const owner = this.state.owner
    if (!owner) return
    this.state.watch.disconnect()
    this.undo(owner)
    if (!owner.hasAttribute('title')) owner.setAttribute('title', this.state.title)
    this.state.owner = null
    this.state.via = ''
  }

  release() {
    this.restore()
    this.hide()
  }

  schedule() {
    clearTimeout(this.state.timer)
    if (this.isOpen() || performance.now() - this.state.hiddenAt < WARM) this.show()
    else this.state.timer = setTimeout(() => this.show(), DELAY)
  }

  show() {
    clearTimeout(this.state.timer)
    const { owner, tip, text } = this.state
    if (!owner?.isConnected || !text || this.state.muted) return this.hide()
    this.state.label.textContent = text
    // measured at the corner, so it wraps at its own width and not at what is left of the window
    tip.style.left = '0px'
    tip.style.top = '0px'
    if (!this.isOpen()) tip.showPopover()
    const root = document.documentElement
    const side = owner.closest('[data-tip-side]')?.dataset.tipSide
    const gap = Number(owner.closest('[data-tip-gap]')?.dataset.tipGap) || undefined
    const place = placeTip(
      owner.getBoundingClientRect(),
      { width: tip.offsetWidth, height: tip.offsetHeight },
      { width: root.clientWidth, height: root.clientHeight },
      { side, gap },
    )
    tip.dataset.side = place.side
    tip.style.left = `${place.left}px`
    tip.style.top = `${place.top}px`
    tip.style.setProperty('--tip-arrow', `${place.arrow}px`)
  }

  hide() {
    clearTimeout(this.state.timer)
    if (!this.isOpen()) return
    this.state.tip.hidePopover()
    this.state.hiddenAt = performance.now()
  }

  // Scrolling or resizing: the next tooltip waits its delay again.
  cool() {
    this.hide()
    this.state.hiddenAt = 0
  }

  // A press or a key acts on the owner: no tooltip on it until the pointer or focus moves on.
  mute() {
    this.state.muted = true
    this.hide()
  }

  isOpen() {
    return Boolean(this.state.tip?.matches(':popover-open'))
  }

  CSS = `:fez {
display:contents;.tip{position:fixed;inset:auto;margin:0;max-width:min(280px,calc(100vw - 2 * var(--space-2)));padding:var(--space-1) var(--space-2);border:0;border-radius:var(--radius-md);background:var(--color-text);box-shadow:var(--shadow-md);color:var(--color-bg);font-size:var(--text-sm);font-weight:var(--weight-medium);line-height:var(--leading-tight);overflow:visible;overflow-wrap:break-word;pointer-events:none;animation:ui-tip-in var(--duration-fast) var(--ease)}.tip-arrow{position:absolute;z-index:-1;width:8px;height:8px;background:inherit;transform:translate(-50%,-50%) rotate(45deg)}.tip[data-side='top'] .tip-arrow{top:100%;left:var(--tip-arrow)}.tip[data-side='bottom'] .tip-arrow{top:0;left:var(--tip-arrow)}.tip[data-side='left'] .tip-arrow{top:var(--tip-arrow);left:100%}.tip[data-side='right'] .tip-arrow{top:var(--tip-arrow);left:0}@keyframes ui-tip-in{from{opacity:0}}
}`

  HTML = `<div class="tip" id="ui-tip" role="tooltip" popover="manual" fez:this="tip"><span class="tip-arrow" aria-hidden="true"></span><span class="tip-text" fez:this="label"></span></div>`
};
}

Fez('ui-tooltip', __fez_component_0);

export default __fez_component_0;
